<template>
  <div class="provider-form-section">
    <h3 v-if="showTitle" class="section-title">{{ title }}</h3>
    
    <!-- Provider Name -->
    <div class="form-group">
      <label :for="`${inputId}-name`">Provider Name <span class="required">*</span></label>
      <input
        type="text"
        :id="`${inputId}-name`"
        v-model="formData.name"
        required
        placeholder="Enter provider name"
        class="form-input"
      />
    </div>

    <!-- Provider Type -->
    <div class="form-group">
      <label :for="`${inputId}-type`">Provider Type <span class="required">*</span></label>
      <select
        :id="`${inputId}-type`"
        v-model="formData.provider_type"
        required
        class="form-input"
      >
        <option value="">Select provider type</option>
        <option v-for="type in providerTypes" :key="type" :value="type">
          {{ type }}
        </option>
      </select>
    </div>

    <!-- Provider Website -->
    <div class="form-group">
      <label :for="`${inputId}-website`">Website <span class="required">*</span></label>
      <input
        type="url"
        :id="`${inputId}-website`"
        v-model="formData.website"
        required
        placeholder="https://example.com"
        class="form-input"
      />
    </div>

    <!-- Provider Description -->
    <div class="form-group">
      <label :for="`${inputId}-description`">Description <span class="required">*</span></label>
      <textarea
        :id="`${inputId}-description`"
        v-model="formData.description"
        required
        rows="3"
        placeholder="Brief description of the provider"
        class="form-input"
      ></textarea>
    </div>

    <!-- Provider Address -->
    <div class="form-group">
      <label :for="`${inputId}-address`">Address</label>
      <input
        type="text"
        :id="`${inputId}-address`"
        v-model="formData.address"
        placeholder="Street address"
        class="form-input"
      />
    </div>

    <div class="form-group">
      <label :for="`${inputId}-address2`">Address Line 2</label>
      <input
        type="text"
        :id="`${inputId}-address2`"
        v-model="formData.address2"
        placeholder="Apartment, suite, etc."
        class="form-input"
      />
    </div>

    <div class="form-row">
      <div class="form-group">
        <label :for="`${inputId}-city`">City</label>
        <input
          type="text"
          :id="`${inputId}-city`"
          v-model="formData.city"
          placeholder="City"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <label :for="`${inputId}-state`">State</label>
        <input
          type="text"
          :id="`${inputId}-state`"
          v-model="formData.state"
          placeholder="State"
          class="form-input"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group">
        <label :for="`${inputId}-postcode`">
          Postcode
          <span v-if="requirePostcode" class="required">*</span>
        </label>
        <input
          type="text"
          :id="`${inputId}-postcode`"
          v-model="formData.postcode"
          :required="requirePostcode"
          placeholder="ZIP/Postal code"
          class="form-input"
        />
      </div>

      <div class="form-group">
        <label :for="`${inputId}-country`">Country</label>
        <input
          type="text"
          :id="`${inputId}-country`"
          v-model="formData.country"
          placeholder="Country"
          class="form-input"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProviderForm',
  props: {
    initialName: {
      type: String,
      default: ''
    },
    inputId: {
      type: String,
      default: 'providerForm'
    },
    requirePostcode: {
      type: Boolean,
      default: false
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: 'Provider Information'
    }
  },
  emits: ['form-ready', 'form-data-changed'],
  data() {
    return {
      formData: {
        name: '',
        provider_type: '',
        website: '',
        description: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        country: ''
      },
      providerTypes: [
        'School',
        'Private Business',
        'Other'
      ]
    }
  },
  computed: {
    isFormReady() {
      const baseReady = !!(
        this.formData.name &&
        this.formData.provider_type &&
        this.formData.website &&
        this.formData.description
      )
      
      if (this.requirePostcode) {
        return baseReady && !!this.formData.postcode
      }
      
      return baseReady
    }
  },
  watch: {
    initialName(newVal) {
      if (newVal) {
        this.formData.name = newVal
      }
    },
    formData: {
      deep: true,
      handler() {
        this.$emit('form-data-changed', this.formData)
        this.$emit('form-ready', this.isFormReady)
      }
    },
    isFormReady(newVal) {
      this.$emit('form-ready', newVal)
    }
  },
  mounted() {
    if (this.initialName) {
      this.formData.name = this.initialName
    }
  },
  methods: {
    getFormData() {
      return { ...this.formData }
    },
    reset() {
      this.formData = {
        name: '',
        provider_type: '',
        website: '',
        description: '',
        address: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        country: ''
      }
    },
    setFormData(data) {
      this.formData = { ...this.formData, ...data }
    }
  }
}
</script>

<style scoped>
.provider-form-section {
  margin-top: 24px;
  padding: 24px;
  background: var(--color-bg-main);
  border-radius: 12px;
  border: 2px solid var(--color-border-light);
}

.section-title {
  font-size: 1.3rem;
  color: var(--color-text-primary);
  margin: 0 0 20px 0;
  font-weight: 600;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.form-group label {
  font-weight: 600;
  color: var(--color-text-primary);
  font-size: 0.95rem;
}

.required {
  color: var(--color-error-main);
}

.form-input {
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border-light);
  border-radius: 6px;
  font-size: 1rem;
  font-family: inherit;
  background-color: var(--color-surface-light);
  color: var(--color-text-primary);
  transition: border-color var(--transition-normal);
}

.form-input:focus {
  outline: none;
  border-color: var(--color-primary-main);
}

textarea.form-input {
  resize: vertical;
  min-height: 100px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
